import { ApolloError } from '@apollo/client';
import { InferredObjectType } from '@ols-packages/core-utilities';
import dayjs from 'dayjs';
import { DurationUnitType } from 'dayjs/plugin/duration';
import {
  castArray,
  isArray,
  isBoolean,
  isEmpty,
  isFunction,
  isNil,
  isNull,
  isNumber,
  isObject,
  isPlainObject,
  isUndefined
} from 'lodash';
import * as process from 'process';
import { Theme } from 'react-select';
import * as yup from 'yup';

import { AppAbility } from '@acl/config';
import { themeColorsArray } from '@context/ThemeColors';
import { ISchemaValidationConfig } from '@src/app/modules/components/forms/types';
import { AnyFunction, MapFunc, Path, Predicate, Primitive, SortByOrder } from '@store/types';

type SanitizeType<T> = {
  targetObj: T,
  options?: {
    valuesToEject?: Primitive[],
    keysToEject?: string[],
    typesToEject?: string[]
  }
};
export type DateTypes = 'year' | 'month' | 'day' | 'date';

export const generateToolTipId = (key: string) => `tooltip-${key.replaceAll(/[^a-zA-Z0-9]/g, '')}`;

// Trim + lowercase. Backend treats emails case-insensitive.
export const normalizeEmail = <T>(value: T): T => {
  if (typeof value !== 'string') return value;
  return value.trim().toLowerCase() as unknown as T;
};

// Normalizes any `email` field, plus `value` when sibling `method === 'EMAIL'`
// (covers the { method, value } contact-method shape without touching phones).
// Only recurses into plain objects so Dates/Files/Blobs pass through intact.
export const normalizeEmailsDeep = <T>(input: T): T => {
  if (Array.isArray(input)) {
    return input.map((item) => normalizeEmailsDeep(item)) as unknown as T;
  }
  if (isPlainObject(input)) {
    const src = input as Record<string, unknown>;
    const isEmailMethod = typeof src.method === 'string' && src.method.toUpperCase() === 'EMAIL';
    const out = Object.entries(src).reduce<Record<string, unknown>>((acc, [key, value]) => {
      if (key === 'email' && typeof value === 'string') {
        acc[key] = normalizeEmail(value);
      } else if (isEmailMethod && key === 'value' && typeof value === 'string') {
        acc[key] = normalizeEmail(value);
      } else {
        acc[key] = normalizeEmailsDeep(value);
      }
      return acc;
    }, {});
    return out as T;
  }
  return input;
};

export const percentageOf = (totalEffort: number, completedEffort: number) => {
  const percentage = (Number(completedEffort) / Number(totalEffort)) * 100;
  if (Number.isNaN(percentage) || !Number.isFinite(percentage)) {
    return 0;
  }
  return percentage;
};

export const isTruthy = (bool: any): bool is true => bool instanceof Boolean && Boolean(bool);
export const isFalsey = (bool: any): bool is false => (bool instanceof Boolean
  ? !Boolean(bool)
  : false);

export const isDate = (val: unknown): boolean => val instanceof Date;

export const isArrayLike = (array: unknown) => !isNull(array)
  && array[Symbol.iterator] instanceof Function;

export const isUUID = (str: string) => {
  // eslint-disable-next-line prefer-regex-literals -- If you use literals linter gets mad stating to use RegExp. If you use RegExp linter gets mad stating to use literals.
  const regex = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');
  return regex.exec(str) !== null;
};

export const isEmptyValues = (value: any): boolean => {
  const isValueLike = (target: any) => {
    if (!isNumber(target) && !isBoolean(target)) {
      return !isEmpty(target);
    }
    return !isNil(target);
  };

  if (isArray(value)) {
    return isEmpty(value.flat().filter(isValueLike));
  }

  if (isPlainObject(value)) {
    return isEmpty(Object.values(value).filter(isValueLike));
  }
  return isNil(value);
};

export const generateAlphaNumericChars = (limit = 10) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  for (let i = 0; i < limit; i += 1) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters[randomIndex];
  }

  return result.toUpperCase();
};

/* eslint-disable no-bitwise */
export const generateUUID = () => (
  `${String(1e7) + String(-1e3) + String(-4e3) + String(-8e3) + String(-1e11)}`)
  .replace(/[018]/g, (bit: string) => (
    Number(bit) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(bit) / 4)))
  ).toString(16));

/* eslint-enable no-bitwise */

export const getUnionValue = <T>(union: InferredObjectType<T>, prefix: string) => {
  const chosenResponse = omitBy(union, (a) => isNull(a) || isUndefined(a));

  return Object.keys(chosenResponse).find(
    (response) => response.startsWith(prefix) && response !== prefix.concat('_id')
  );
};

/**
 * Returns the column size for a given item index in an array.
 * @param {number} itemIndex - The index of the item.
 * @param {number} arrLength - The length of the array.
 * @returns {number} - The column size.
 */
export const getColumnSize = (itemIndex: number, arrLength: number) => {
  const index = itemIndex + 1;

  // ** If only one condition total, col=12
  if (itemIndex <= 0 && index === arrLength) {
    return 12;
  }

  // ** If more than one item and last item is only one in row, col=12
  if (index > 1 && index === arrLength) {
    return Boolean(index % 2) ? 12 : 6;
  }

  return 6;
};

export const stripProperty = <T>(data: T, key: keyof T): Omit<T, typeof key> => {
  if (Array.isArray(data)) {
    return data.map((item) => stripProperty(item, key)) as any;
  }
  if (isObject(data) && !isNull(data)) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [key]: prop, ...rest } = data as Record<keyof T, any>;
    const strippedData: Partial<T> = {};

    Object.keys(rest).forEach((property) => {
      strippedData[property] = stripProperty(rest[property], key);
    });

    return strippedData as Omit<T, typeof key>;
  }
  return data;
};

export const convertICD10ToDotNotation = (icd10Code: string): string => {
  const category = icd10Code.substring(0, 1);
  const subcategory = icd10Code.substring(1, 3);
  const extension = icd10Code.substring(3);

  let dotNotation = category + subcategory;

  if (extension) {
    dotNotation = `${dotNotation}.${extension}`;
  }

  return dotNotation;
};

export const createYupSchema = (
  schema: Record<string, any>,
  config: { validationConfig?: ISchemaValidationConfig }
) => {
  const validationSchema = schema;
  if (!config?.validationConfig) {
    return validationSchema;
  }
  const { validationConfig: { id, validationType, validations = [] } } = config;

  if (!yup[validationType]) {
    return validationSchema;
  }

  let validator = yup[validationType]();
  validations.forEach(({ params, type }) => {
    if (!validator[type] || !params) {
      return;
    }
    validator = validator[type](...params);
  });

  validationSchema[id] = validator;
  return validationSchema;
};

export const getApolloExtensionError = (error: ApolloError) => {
  let validationError: { message: string, code: string, path: string } | undefined;

  const foundExtensions = error.graphQLErrors.at(0)!.extensions as any;

  if (!isEmpty(foundExtensions?.validationErrors)) {
    validationError = foundExtensions.validationErrors.at(0);
  }
  return {
    message: validationError?.message ?? error.message,
    code: validationError?.code,
    path: validationError?.path
  };
};
// export const yupFromGraphQL = (inputTypeName: string) => {
//   const inputs = deepGet(graphqlSchema, '__schema.types') as unknown as Array<{
//     kind: string,
//     name: string,
//     enumValues: Array<{ name: string }> | null
//     inputFields: Array<{
//       name: string,
//       type: {
//         kind: string
//         name: string,
//         ofType?: {
//           kind: string,
//           name: string,
//         } | null
//       }
//     }>
//   }>;
//   const shape = {};
//   const targetField = inputs.find(
//     (field) => field.name.toLowerCase() === inputTypeName.toLowerCase()
//   );
//
//   const generateSchema = (field) => {
//     const { name, type } = field;
//
//     switch (type.kind) {
//       case 'SCALAR':
//         shape[name] = yup.string();
//         break;
//       case 'ENUM':
//         shape[name] = yup.string().oneOf(targetField.enumValues.map((enumValue) => enumValue.name));
//         break;
//       case 'LIST':
//         shape[name] = yup.array().of(yupFromGraphQL(type.ofType.name));
//         break;
//       case 'NON_NULL':
//         yupFromGraphQL(type.ofType.name);
//         shape[name] = shape[name] ? shape[name].required() : yup.string().required();
//         break;
//       case 'INPUT_OBJECT':
//         shape[name] = yupFromGraphQL(type.name);
//         break;
//       default:
//         throw new Error(`Unsupported kind: ${type.kind}`);
//     }
//   };
//   if (!targetField?.inputFields){
//     generateSchema({ type: { ...targetField }, ...targetField });
//   }
//
//   targetField?.inputFields?.forEach((field) => {
//     generateSchema(field);
//   });
//
//   return yup.object().shape(shape);
// };

export const getNestedIndexObject = <T>(
  obj: T,
  path: string,
  defaultValue: any = null
): T[keyof T] => {
  const keys = path.split('.');
  let nestedObj = obj;

  keys.forEach((key) => {
    const arrKeyIndex = /\[(\d+)\]$/.exec(key);
    if (arrKeyIndex) {
      const arrKey = key.slice(0, key.indexOf('['));
      const index = Number(arrKeyIndex[1]);
      nestedObj = nestedObj?.[arrKey]?.[index];
    } else {
      nestedObj = nestedObj?.[key];
    }

    if (isNull(nestedObj) || isUndefined(nestedObj)) {
      return defaultValue;
    }
  });

  return nestedObj as unknown as T[keyof T];
};

export const deepGet = <T extends T[] | InferredObjectType<unknown>, >(
  target: T,
  keys: string | Array<string | number>,
  delimiter = '.'
): T[keyof T] => {
  let queryKey = keys;

  if (typeof queryKey === 'string') {
    queryKey = queryKey.split(delimiter);
  }
  if (isArrayLike(target)) {
    // Might want to return an array of values
  }

  return queryKey.reduce((xs, x) => (xs && xs[x] ? xs[x] : null), target);
};

export const equals = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true;
  }

  if (typeof a === 'string') {
    if (typeof b !== 'string') {
      return false;
    }
    if (a.trim().toLowerCase() === b.trim().toLowerCase()) {
      return true;
    }
    return a === b;
  }
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) {
    return false;
  }
  return keys.every((key) => equals(a[key], b[key]));
};

export const mapObject = <T = any>(arr: T[], fn: MapFunc<T>) => arr.reduce((acc, el, i) => {
  acc[el] = fn(el, i, arr);
  return acc;
}, {} as any);

export const pipeFunctions = (...fns: AnyFunction[]) => fns.reduce(
  (previous, current) => (...args) => current(previous(...args))
);

export const sortByKey = <T = any>(
  arr: T[],
  key: string,
  order: SortByOrder
) => Array.from(arr).sort(
    (a: any, b: any) => order * String(
      deepGet(a, key)
    ).localeCompare(String(deepGet(b, key)))
  );

export const sortByOrder = <T, K extends T[keyof T]>(
  array: T[],
  path: Path<T>,
  order: K[]
): T[] => array.slice().sort((a, b) => {
    const suitabilityIndexA = order.indexOf(a[path as keyof typeof order]);
    const suitabilityIndexB = order.indexOf(b[path as keyof typeof order]);
    return suitabilityIndexA - suitabilityIndexB;
  });

/**
 * @summary Returns an array of values from selected path
 * @param arr
 * @param path
 */
export const pluck = <T, K extends keyof T>(arr: T[], path: Path<T>): Array<T[K]> => {
  const keys = String(path).split('.');
  return castArray(arr).reduce((result, obj) => {
    const value = keys.reduce((acc, key) => {
      if (!acc) {
        return undefined;
      }
      return acc[key];
    }, obj);

    return [...result, value as T[K]];
  }, []);
};

export const combine = (...fns: AnyFunction[]) => fns.map((f) => (f())).flat();

export const omitBy = <T>(obj: T, fn: Predicate<InferredObjectType<T>>) => Object.keys(obj)
  .filter((k) => !fn(obj[k], k as any))
// eslint-disable-next-line no-return-assign,no-sequences
  .reduce((acc, key) => ((acc[key] = obj[key]), acc), {} as InferredObjectType<T>);

export const remove = (arr: unknown[], func: Predicate<any>) => (Array.isArray(arr)
  ? arr.filter(func).reduce((acc: unknown[], val) => {
    arr.splice(arr.indexOf(val), 1);
    return acc.concat(val);
  }, [])
  : []);

export const pickBy = <T = any>(obj: T, fn: AnyFunction) => Object.keys(obj)
  .filter((k) => fn(obj[k], k))
// eslint-disable-next-line no-return-assign,no-sequences
  .reduce((acc, key) => ((acc[key] = obj[key]), acc), {} as InferredObjectType<T>);

export const unflattenObject = <T>(
  obj: InferredObjectType<T>
) => Object.keys(obj).reduce((prev, curr) => {
    curr.split('.').reduce(
      (acc, currentValue, currentIndex, keys) => {
        const target = acc;
        if (target[currentValue]) {
          return target[currentValue];
        }
        if (Number.isNaN(Number(keys[currentIndex + 1]))) {
          target[currentValue] = keys.length - 1 === currentIndex
            ? obj[curr]
            : {};
        } else {
          target[currentValue] = [];
        }
        return target[currentValue];
      },
      prev
    );
    return prev;
  }, {} as InferredObjectType<T>);

export const unique = <T>(arr: T[]) => [...new Set(arr)];

/**
 * Check if content of array all match
 * @param arrays
 */
export const arrayContentsMatch = (...arrays: any[][]) => {
  const arrSet = new Set(arrays.flat());

  return arrays.every((arr) => arr.length === arrSet.size);
};

export const orderBy = <T = Record<string, unknown>>(
  arr: T[],
  props: Array<keyof T>,
  orders?: Array<'asc' | 'desc'>
) => [...arr].sort((a, b) => props.reduce((acc, prop, i) => {
    if (acc === 0) {
      const [p1, p2] = orders && orders[i] === 'desc'
        ? [b[prop], a[prop]]
        : [a[prop], b[prop]];
      // eslint-disable-next-line
    acc = p1 > p2 ? 1:p1 < p2 ? -1:0;
    }
    return acc;
  }, 0));

export const flattenObject = <T, >(obj: InferredObjectType<T>, prefix = '') => Object.keys(obj)
  .reduce((acc, key) => {
    const pre = prefix.length ? `${prefix}.` : '';
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      Object.assign(acc, flattenObject(obj[key], pre + key));
    } else {
      acc[pre + key] = obj[key];
    }
    return acc;
  }, {} as T);

export const sanitize = <T>(
  {
    targetObj,
    options
  }: SanitizeType<T>
): T => {
  const union = structuredClone(targetObj);
  const sanitizeObj = (obj: T): void => Object.keys(obj)
    .forEach((key) => {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        return sanitizeObj(obj[key] as typeof obj);
      }
      if (options) {
        if (options.valuesToEject?.includes(obj[key])) {
        // eslint-disable-next-line no-param-reassign
          delete obj[key];
        }
        if (options.keysToEject?.includes(key)) {
        // eslint-disable-next-line no-param-reassign
          delete obj[key];
        }
        if (options.typesToEject) {
          if (options.typesToEject.includes(typeof obj[key])) {
          // eslint-disable-next-line no-param-reassign
            delete obj[key];
          }
        }
      }
    });

  sanitizeObj(union);
  return union;
};

export function levenshtein(a: string, b: string): number {
  // Create a matrix of size (lenA+1) x (lenB+1)
  const matrix: number[][] = Array.from({ length: a.length + 1 }, () => Array(b.length + 1)
    .fill(0));

  // Initialize the first row and column
  for (let i = 0; i <= a.length; i += 1) {
    matrix[i][0] = i;
  }
  for (let j = 0; j <= b.length; j += 1) {
    matrix[0][j] = j;
  }

  // Fill in the rest of the matrix
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // Deletion
        matrix[i][j - 1] + 1, // Insertion
        matrix[i - 1][j - 1] + cost // Substitution
      );
    }
  }

  return matrix[a.length][b.length];
}

/**
 * Computes a similarity score between two strings based on Levenshtein distance
 * @param a - First string to compare
 * @param b - Second string to compare
 * @returns Similarity score between 0 and 1.
 */
export const stringEquality = (a: string, b: string): number => {
  const distance = levenshtein(a, b);
  const maxStringLength = Math.max(a.length, b.length);
  return 1 - distance / maxStringLength;
};

export const matches = <T extends Record<string, any>, >(obj: T, source: T) => Object.keys(source)
  .every(
    (key) => Object.hasOwn(obj, key) && obj[key] === source[key]
  );

export const sum = (...arr: number[]) => [...arr].reduce((acc, val) => acc + val, 0);

export const roundToNearest = (num: number, stepSize = 10, direction: 'up' | 'down' = 'up') => {
  const operator = direction === 'up' ? 'ceil' : 'floor';
  return Math[operator](num / stepSize) * stepSize;
};

export const generateRandomValue = (
  min: number,
  max: number
) => Math.floor(Math.random() * (max - min + 1) + min);

export const getRandomColor = (light = false) => {
  let colors = themeColorsArray;
  const invalidLightColors: string[] = ['dark'];
  if (light) {
    colors = colors.filter((color) => !invalidLightColors.includes(color));
    return `light-${colors[generateRandomValue(0, colors.length - 1)]}`;
  }
  return colors[generateRandomValue(0, colors.length - 1)];
};

export const getRandomHexColor = () => {
  const hexChars = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i += 1) {
    color += hexChars[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const sleep = (ms: number) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

export const truncateString = (
  str: string,
  max: number = str.length,
  ellipsisString = '...'
) => (str.length >= max
  ? str.slice(0, max >= ellipsisString.length
    ? max - ellipsisString.length
    : max).concat(ellipsisString)
  : str);

export const stringHasUpperCase = (string: string) => Boolean(string.split('')
  .find((letter) => letter.toUpperCase() === letter));

export const convertCamelToSnakeCase = (string: string) => {
  const lastLetter = string.split('').pop();
  if (lastLetter === 'D') {
    return string.split(string.split('').pop()!).shift()!.toString().replace(/([A-Z])/g, '_$1')
      .trim().toLowerCase()
      .concat(string.split('').pop()!.toLocaleLowerCase());
  }
  return string.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

export const convertObjectKeysToSnake = <T extends Record<string, unknown>>(obj: T) => {
  Object.entries(obj).forEach(([key, value]) => {
    const transformedKey = stringHasUpperCase(key) && convertCamelToSnakeCase(key);
    if (transformedKey) {
      delete Object.assign(obj, { [`${transformedKey}`]: value })[key];
    }
  });
  return obj;
};

export const uppercaseFirstLetter = (string: string)
: string => string && string.charAt(0).toUpperCase() + string.slice(1);

export const upperEveryFirstLetter = (sentence: string) => sentence.split(' ')
  .map((word) => word[0]?.toUpperCase() + word.substring(1)).join(' ');

export const capitalize = (word: string) => {
  if (isNil(word)) {
    return word;
  }
  const phrase = String(word);

  return phrase.charAt(0).toUpperCase()
    .concat(phrase.slice(1).toLowerCase());
};

export const isObjEmpty = <T>(obj: T) => obj && Object.keys(obj)?.length === 0;

export const passwordRegexExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@$%^&*])(?=.{8,})/;
export const last = <T>(arr: T[]) => arr[arr.length - 1];
export const first = <T>(arr: T[]) => arr[0];

export const phoneRegExp = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/;
export const normalizePhoneNumber = (input: string) => input.replace(/\D+/g, '')
  .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');

export const formatEnum = (enumStr: string) => uppercaseFirstLetter(enumStr.trim()
  .toLowerCase()
  .replaceAll('_', ' '));

export const elapsedDuration = (timestamp: string) => {
  const thresholds = [
    { limit: 60, unit: 'second', plural: 'seconds' },
    { limit: 60, unit: 'minute', plural: 'minutes' },
    { limit: 24, unit: 'hour', plural: 'hours' },
    { limit: 30, unit: 'day', plural: 'days' }
  ];

  let display = 'Over a month ago';

  const from = dayjs(timestamp);
  const current = dayjs();

  const difference = current.diff(from, 'second');

  const duration = dayjs.duration(difference, 'seconds');
  // eslint-disable-next-line no-restricted-syntax
  for (const { limit, unit, plural } of thresholds) {
    const value = Math.floor(duration.as(unit as DurationUnitType));

    if (value < limit) {
      const unitDisplay = value > 1 ? plural : unit;
      display = `${value} ${unitDisplay} ago`;
      return display;
    }
  }

  return display;
};

export const getFutureDates = (dateType: DateTypes, futureCount: number, format?: string) => {
  const array = [];
  for (let index = 0; index <= futureCount; index += 1) {
    const initialDate = dayjs();
    const nextDate = dayjs(initialDate)[dateType](initialDate[dateType]() + index);
    format ? array.push(dayjs(nextDate).format(format)) : array.push(dayjs(nextDate));
  }
  return array;
};
export const getPreviousDates = (dateType: DateTypes, prevCount: number, format: string) => {
  const array = [];
  for (let index = 0; index <= prevCount; index += 1) {
    const initialDate = dayjs();
    const prevDate = dayjs(initialDate)[dateType](initialDate[dateType]() - index);
    array.push(dayjs(prevDate).format(format));
  }
  return array;
};

export const resolveNestedValue = <T>(path: string, obj: T) => {
  const properties = path.split('.');
  return properties.reduce((prev: string | any, curr: string) => prev && prev[curr], obj);
};

export const convertEnumToSelectionArray = (enumPassed: any)
: Array<{ value: number | string; label: string }> => {
  const selectionArray: Array<{ value: number; label: string }> = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const key of Object.values(enumPassed)) {
    const index = Object.values(enumPassed).flat().indexOf(key);
    selectionArray.push({
      label: String(key).replaceAll('_', ' '), value: index
    });
  }
  return selectionArray
    .filter((selection) => Number.isNaN(+selection.label))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const convertTextEnumToSelectionArray = (enumPassed: any)
: Array<{ value: number | string; label: string }> => {
  const selectionArray: Array<{ value: string; label: string }> = [];

  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const key in enumPassed) {
    selectionArray.push({
      label: key.replaceAll('_', ' '), value: key
    });
  }
  return selectionArray.sort((a, b) => a.label.localeCompare(b.label));
};

export const preventOverflow = (
  array: string[],
  maxLength: number,
  separator?: string
): string[] => {
  const arrLength = array.join(separator ?? ',').length;

  // eslint-disable-next-line no-unused-expressions
  if (maxLength < arrLength) {
    // eslint-disable-next-line no-unused-expressions,@typescript-eslint/no-unused-expressions
    array.pop() && preventOverflow(array, maxLength);
  }

  return array;
};

export const deserialize = (target: any) => {
  try {
    if (isEmpty(target)) {
      return target;
    }

    if (typeof target === 'string') {
      return JSON.parse(target);
    }
    return target;
  } catch {
    return target;
  }
};

export const calculateTax = (price: number, taxRate: number) => ((price * taxRate) / 100);

export const calculateDiscountSavings = (
  total: number,
  discountRate: number
) => (total * discountRate) / 100;

export const calculateSubTotal = (prices: number[], discountRate?: number) => {
  const totalPrice = prices.reduce((
    previousPrice,
    currentPrice
  ) => previousPrice + currentPrice, 0);
  return totalPrice - calculateDiscountSavings(totalPrice, discountRate ?? 0);
};

export const calculateCartTotal = (prices: number[], taxRate: number, discountRate?: number) => {
  const subTotal = calculateSubTotal(prices, discountRate);
  return subTotal + calculateTax(subTotal, taxRate);
};

// Dedupes by a key — by default, by object reference (`Apollo Reference.__ref`).
// Falling back to JSON.stringify only for non-normalized rows. JSON.stringify is
// O(n*size) and key-order-sensitive, so prefer passing an explicit `keyOf` for any
// hot path (paginated lists in the cache).
export const removeDuplicateObjects = <T>(
  arr: readonly T[],
  // `__ref` is Apollo's own normalized-reference key, not a private convention
  // on our side, so dangling-underscore is expected here.
  // eslint-disable-next-line no-underscore-dangle
  keyOf: (item: T) => string | number = (item: any) => item?.__ref ?? JSON.stringify(item)
): T[] => {
  const seen = new Map<string | number, T>();
  arr.forEach((item) => {
    const key = keyOf(item);
    if (!seen.has(key)) seen.set(key, item);
  });
  return [...seen.values()];
};

// SessionStorage helpers. All access is guarded:
//   - reads `try` JSON.parse so corrupt entries return null instead of crashing app boot
//   - writes `try` setItem so a quota-exceeded error doesn't propagate up into a render
//
// Both functions are SSR-safe (no-op when window/sessionStorage is undefined) which
// keeps the helpers usable from module-load reactive var initializers without guards.
const isStorageAvailable = (): boolean => typeof window !== 'undefined' && Boolean(window.sessionStorage);

export const getStorageItem = <T = any>(key: string): T | null => {
  if (!isStorageAvailable()) return null;
  try {
    const item = sessionStorage.getItem(key);
    if (item === null || item === '') return null;
    return JSON.parse(item) as T;
  } catch (err) {
    // Corrupt JSON or storage access denied — drop the bad entry so subsequent reads
    // don't keep failing on the same payload, and return null so callers fall through
    // to their default value.
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[getStorageItem] failed to read "${key}":`, err);
    }
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
    return null;
  }
};

export const setStorageItem = <T>(key: string, value: T): void => {
  if (!isStorageAvailable()) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    // Quota exceeded, private browsing mode, or value not serializable. Surface in dev,
    // swallow in prod so a non-critical persistence failure can't crash the user's session.
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[setStorageItem] failed to write "${key}":`, err);
    }
  }
};

// localStorage variants for values that must outlive the tab session
// (auth/user data, theme preference). Same try/catch contract as the
// sessionStorage helpers above.
const isLocalStorageAvailable = (): boolean => typeof window !== 'undefined' && Boolean(window.localStorage);

export const getLocalStorageItem = <T = any>(key: string): T | null => {
  if (!isLocalStorageAvailable()) return null;
  try {
    const item = localStorage.getItem(key);
    if (item === null || item === '') return null;
    return JSON.parse(item) as T;
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[getLocalStorageItem] failed to read "${key}":`, err);
    }
    try { localStorage.removeItem(key); } catch { /* ignore */ }
    return null;
  }
};

export const setLocalStorageItem = <T>(key: string, value: T): void => {
  if (!isLocalStorageAvailable()) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[setLocalStorageItem] failed to write "${key}":`, err);
    }
  }
};

// ** Returns K format from a number
export const kFormatter = (num: number) => (num > 999 ? `${(num / 1000).toFixed(1)}k` : num);

// ** Converts HTML to string
export const htmlToString = (html: string) => html.replace(/<\/?[^>]+(>|$)/g, '');

// ** Checks if the passed date is today
const isToday = (date: Date) => {
  const today = new Date();
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  );
};

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (
  value: string,
  formatting: Record<string, unknown> = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }
) => {
  if (!value) {
    return value;
  }
  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value));
};

export const normalizeTimestamp = (timestamp: string | Date) => {
  const stamp = dayjs(timestamp);

  stamp.set('date', dayjs().get('date'))
    .set('hour', stamp.get('hour'))
    .set('minute', stamp.get('minute'))
    .set('second', 0)
    .set('millisecond', 0);

  return stamp;
};

export const getParsedHour = (hour: string) => Number(hour.split(':').shift());
export const getParsedMinute = (time: string) => Number(time.split(':').pop());

export const formatTime = (
  time: string,
  day?: Date | number,
  format?: string
) => dayjs(day).hour(getParsedHour(time))
  .set('minute', getParsedMinute(time)).format(format ?? 'hh:mm a');

// ** Returns short month of passed date
export const formatDateToMonthShort = (value: string, toTimeForCurrentDay = true) => {
  const date = new Date(value);
  let formatting: { hour: string; minute: string, month?: string, day?: string } = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  };

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: 'numeric', minute: 'numeric' };
  }

  // @ts-ignore
  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value));
};

export const clone = <T>(obj: T): T => {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[clone] JSON round-trip failed; returning shallow copy:', err);
    }
    // Shallow fallback — callers expect a new reference. The original
    // console.error also dumped the full `obj`, which often contains PHI.
    return { ...obj };
  }
};

export const debounce = <Args extends unknown[], T>(
  fn: (...args: Args) => T, ms = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

export const uniqueBy = <T>(
  arr: T[],
  property: keyof T
) => {
  if (isNull(arr) || isUndefined(arr) || !isArrayLike(arr)) {
    return [];
  }
  return arr.filter(
    (item, index, self) => index === self.findIndex(
      (o) => o[property] === item[property]
    )
  );
};

export const includesAny = <T>(
  arr: T[],
  predicate: (value: T
  ) => boolean = Boolean
) => isArrayLike(arr) && arr.some(predicate);

export const deepMerge = <T, S>(target: T, source: S): T => {
  const clonedTarget = clone(target);
  Object.entries(source).forEach(([key, value]) => {
    if (value !== null && typeof value === 'object') {
      clonedTarget[key] = deepMerge(clonedTarget[key], value);
    } else {
      clonedTarget[key] = value;
    }
  });
  return clonedTarget;
};

export const enumToSelectOptions = <T extends Record<keyof T, string>>(
  enumObj: T,
  transformOptionValue?: (option: string) => string,
  transformOptionLabel?: (option: string) => string
) => Object.entries(enumObj)
    .map((option) => ({
      label: transformOptionLabel
        ? transformOptionLabel(String(last(option)))
        : formatEnum(String(last(option))),
      value: transformOptionValue
        ? transformOptionValue(String(last(option)))
        : first(option)
    }));

export const getHomeRouteForLoggedInUser = (userType: string) => {
  if (userType === 'admin') {
    return '/';
  }
  if (userType === 'patient') {
    return '/general/not-authorized';
  }
  return `/auth/${userType}/login`;
};

// ** React Select Theme Colors
export const selectThemeColors = (theme: Theme): Theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: '#7367f01a', // for option hover bg-color
    primary: '#7367f0', // for selected option bg-color
    neutral0: '#fff',
    neutral10: '#007774', // for tags bg-color
    // neutral10: '#7367f0', // for tags bg-color
    neutral20: '#ededed', // for input border-color
    neutral30: '#ededed' // for input hover border-color
  }
});

export const isAsyncFunction = (func: any): boolean => func instanceof Promise
  || (func && typeof func.then === 'function')
  || (func?.constructor?.name === 'AsyncFunction');

export const asyncIterator = async <P = unknown, T = unknown>(
  arr: T[],
  cb: (arg: T, index: number) => Promise<P | void>
) => Promise.all(arr.map(cb));

/**
 * @returns [promise, resolve, reject]
 */
export const usePromise = <T>(): [
  Promise<T>,
  (value: (T | PromiseLike<T>)) => void,
  (reason?: any) => void
] => {
  let resolvePromise!: (value: T | PromiseLike<T>) => void;
  let rejectPromise!: (reason?: any) => void;
  const promise = new Promise<T>((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });
  return [promise, resolvePromise, rejectPromise];
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const promisify = <T = any>(func: Function) => (
  ...args: any[]
  // eslint-disable-next-line no-promise-executor-return
): Promise<T> => new Promise<T>((resolve, reject) => func(...args, (err: Error, result: T) => (err
  ? reject(err)
  : resolve(result as T))));

export const wrapAsyncFunction = <Args extends unknown[]>(fn:
(...args: Args) => Promise<unknown>, _errorHandler?: boolean): (...args: Args) =>
  void => (...args) => {
    fn(...args).catch((err) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[wrapAsyncFunction] async handler rejected:', err);
      }
    });
  };

export const maybePromise = <Args extends unknown[], T>(
  fn: (...args: Args) => T
): ((...args: Args) => Promise<T> | T) => (...args: Args): Promise<T> | T => {
    if (isFunction(fn)) {
      const result = fn(...args);
      if (result instanceof Promise || isAsyncFunction(result)) {
        return wrapAsyncFunction(result as () => Promise<T>) as T; // You need to define wrapAsyncFunction
      }
      return result;
    }
    if (process.env.REACT_APP_MODE === 'DEVELOPMENT') {
      throw new TypeError(
        `Passed argument must be of type Function. Instead got ${typeof fn}`
      );
    }
    return fn;
  };
export const promiseHandle = <T, >(promise: Promise<T> | Promise<unknown>): T => {
  promise.catch((err) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[promiseHandle] unhandled rejection:', err);
    }
  });
  return undefined;
};

export const validateRouteActions = (route: {
  meta: {
    actions: any[];
    resource: any;
  }
}, ability: AppAbility) => {
  // Set total action to verify
  const totalActions = route.meta.actions.length;
  // Set counter
  let validatedAction = 0;
  // iterate over and increment up for each match
  route.meta?.actions.forEach((action) => {
    validatedAction += ability.can(action, route.meta.resource) ? 1 : 0;
  });
  // if all actions are validated route returns true
  return validatedAction === totalActions;
};
